/**
 * `/demo` — the password-less sign-in page behind the AI Summit QR code.
 *
 * One question, one button. Someone scanning a poster between sessions should not meet a password field, a
 * signup link and a forgot-password link on the way in.
 *
 * THE AUTH IS NOT NEW. This reuses the deployed `/request-login-code` + `login-with-code` pair, so the code's
 * generation, its ten-minute expiry and its single-use behaviour are the ones already running in production.
 * A "magic link" is that same code carried in the URL — which is why this page adds a surface for a person to
 * look at and no surface for an attacker.
 *
 * THE COPY IS PINNED TO THE BACKEND. `users.generate_otp` issues a code with `timedelta(minutes=10)`, and
 * `verify_otp` nulls it on success. So the page says ten minutes and single use, and a test asserts the
 * number rather than trusting whoever edits this file next.
 *
 * REDEMPTION GOES THROUGH THE AUTH CONTEXT, never a bare fetch. A local fetch sets the session cookie and
 * never tells AuthContext, so the route guard sees no user and bounces back to /auth — reported on DEV as
 * "kept redirecting to auth page than i reloaded and finally got in". `Login.tsx` carries the same warning.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Zap, Loader2, CheckCircle2 } from 'lucide-react';

import { useAuth } from '../../context/AuthContext';

/** Matches `users.generate_otp`'s `timedelta(minutes=10)`. Asserted by DemoLogin.test.tsx. */
export const MAGIC_LINK_TTL_MINUTES = 10;

/** Query parameters the emailed link carries. Short because the link is printed and retyped by hand. */
export const EMAIL_PARAM = 'e';
export const CODE_PARAM = 'c';

const AFTER_SIGN_IN = '/';

const BRAND_TEAL = '#07E5D1';
const BRAND_TEAL_DARK = '#06D4C2';

const COPY = {
  badge: 'AI Summit Portal',
  heading: 'Sign in to a BluePill demo account',
  subheading: "No password — we'll email you a secure link instead.",
  emailLabel: 'Email address',
  emailPlaceholder: 'you@company.com',
  send: 'Send magic link',
  sending: 'Sending…',
  signingIn: 'Signing you in…',
  sentHeading: 'Check your email',
  sentBody: 'If that address can use the demo, a sign-in link is on its way.',
  again: 'Send magic link',
  footnote: `Link expires in ${MAGIC_LINK_TTL_MINUTES} minutes and can only be used once.`,
  missingEmail: 'Enter your email address first.',
  requestFailed: 'Could not send a link. Try again.',
  unreachable: 'Could not reach the server. Check your connection and try again.',
} as const;

type Phase = 'form' | 'sending' | 'sent' | 'redeeming';

const DemoLogin: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { loginWithCode, requestSignInLink } = useAuth();

  const [email, setEmail] = useState(params.get(EMAIL_PARAM) ?? '');
  const [phase, setPhase] = useState<Phase>('form');
  const [error, setError] = useState<string | null>(null);

  const requestLink = useCallback(async (address: string) => {
    // The backend answers identically for every address, so it cannot tell us the field was empty. Without
    // this check, someone who typed nothing is told a link is on its way.
    if (!address.trim()) {
      setError(COPY.missingEmail);
      return;
    }
    setError(null);
    setPhase('sending');

    // Wrapped because an exception here used to leave the page stuck: `setPhase('sending')` had already run,
    // the promise rejected unhandled, and the button sat disabled on "Sending…" with nothing on screen.
    // That is exactly what shipped — "DemoLogin.tsx:76 Uncaught (in promise) TypeError: a is not a function",
    // because the context method was missing from the deployed AuthContext. A page whose only action can fail
    // silently is worse than one that fails loudly.
    let result: { sent: boolean; signedIn: boolean; message: string };
    try {
      result = await requestSignInLink(address.trim());
    } catch (thrown) {
      console.error('Sign-in link request threw', thrown);
      setError(COPY.requestFailed);
      setPhase('form');
      return;
    }

    if (result.signedIn) {
      // DEV only: the server signed the account in and sent nothing, because that database holds real brand
      // addresses. The context has already adopted the session, so navigating is all that is left.
      navigate(AFTER_SIGN_IN, { replace: true });
      return;
    }
    if (!result.sent) {
      setError(result.message || COPY.requestFailed);
      setPhase('form');
      return;
    }
    // Shown even when the address has no account. The backend is deliberately indistinguishable here and
    // this URL is printed on a poster, so the page must not reveal which addresses exist.
    setPhase('sent');
  }, [navigate, requestSignInLink]);

  // A link carries both halves or it is not a link. `?e=` alone is someone editing a URL, and redeeming an
  // empty code would spend the attempt and show a failure the person did not cause.
  const linkEmail = params.get(EMAIL_PARAM);
  const linkCode = params.get(CODE_PARAM);
  const redeemed = useRef(false);

  useEffect(() => {
    if (!linkEmail || !linkCode || redeemed.current) return;
    redeemed.current = true;
    setPhase('redeeming');
    (async () => {
      const result = await loginWithCode(linkEmail, linkCode);
      if (result.redirect) {
        // A full page load, not a router navigate: the marketplace carries its own auth context and a
        // client-side transition lands in a shell that never authenticated.
        window.location.href = result.redirect;
        return;
      }
      if (!result.success) {
        // Back to the form rather than a dead end — an expired link at a booth must be one tap from a new one.
        setError(result.message);
        setPhase('form');
        return;
      }
      navigate(AFTER_SIGN_IN, { replace: true });
    })();
  }, [linkEmail, linkCode, loginWithCode, navigate]);

  const busy = phase === 'sending' || phase === 'redeeming';

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-12">
      <div className="w-full max-w-md rounded-3xl bg-white p-8 shadow-xl shadow-slate-200/60 sm:p-10">
        <span
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold uppercase tracking-widest"
          style={{ backgroundColor: `${BRAND_TEAL}22`, color: BRAND_TEAL_DARK }}
        >
          <Zap className="h-4 w-4" aria-hidden="true" />
          {COPY.badge}
        </span>

        <h1 className="mt-6 text-3xl font-bold leading-tight text-slate-900">{COPY.heading}</h1>
        <p className="mt-3 text-base text-slate-500">{COPY.subheading}</p>

        {phase === 'sent' ? (
          <div className="mt-8">
            <div className="flex items-start gap-3 rounded-2xl bg-slate-50 p-4">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" style={{ color: BRAND_TEAL_DARK }} aria-hidden="true" />
              <div>
                <p className="font-semibold text-slate-900">{COPY.sentHeading}</p>
                <p className="mt-1 text-sm text-slate-500">{COPY.sentBody}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setPhase('form')}
              className="mt-6 w-full rounded-2xl border border-slate-200 py-3 font-semibold text-slate-700 transition-colors hover:bg-slate-50"
            >
              {COPY.again}
            </button>
          </div>
        ) : (
          <form
            className="mt-8"
            onSubmit={(event) => {
              event.preventDefault();
              void requestLink(email);
            }}
          >
            <label htmlFor="demo-email" className="block text-sm font-semibold text-slate-900">
              {COPY.emailLabel}
            </label>
            <div className="relative mt-2">
              <Mail
                className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
                aria-hidden="true"
              />
              <input
                id="demo-email"
                name="email"
                type="email"
                autoComplete="email"
                autoFocus
                disabled={busy}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={COPY.emailPlaceholder}
                className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-4 pl-12 pr-4 text-base text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-slate-300 focus:bg-white"
              />
            </div>

            {error && (
              <p role="alert" className="mt-4 rounded-2xl bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={busy}
              className="mt-8 flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-lg font-bold text-white transition-opacity disabled:opacity-70"
              style={{ backgroundColor: BRAND_TEAL_DARK }}
            >
              {busy && <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />}
              {phase === 'redeeming' ? COPY.signingIn : phase === 'sending' ? COPY.sending : COPY.send}
            </button>
          </form>
        )}

        <p className="mt-6 text-center text-sm text-slate-400">{COPY.footnote}</p>
      </div>
    </div>
  );
};

export default DemoLogin;
